import { createConsumer } from '@rails/actioncable';

import { TICKETS_ORIGIN } from 'constants/api';

/**
 * ActionCable consumer manager.
 * Encapsulates singleton lifecycle, token-aware connection rotation,
 * and WebSocket URL construction.
 *
 * @class CableConsumer
 */
class CableConsumer {
    consumer = null;

    currentToken = null;

    /**
     * Build the ActionCable WebSocket URL from the ticketing origin
     * and the provided authentication token.
     *
     * @method buildUrl
     * @param  {string} token The access token for authentication
     * @return {string}       The full cable URL with token param
     */
    buildUrl(token) {
        const base = TICKETS_ORIGIN.replace(/\/$/, '');
        const wsBase = base.replace(/^https:/, 'wss:').replace(/^http:/, 'ws:');
        return `${wsBase}/cable?token=${token}`;
    }

    /**
     * Get or create the ActionCable consumer instance.
     * If the token has changed since the last call, the existing
     * consumer is disconnected and a new one is created.
     *
     * @method getConsumer
     * @param  {string}   token The access token for authentication
     * @return {Object}         The ActionCable consumer instance
     */
    getConsumer(token) {
        if (this.consumer && this.currentToken !== token) {
            this.consumer.disconnect();
            this.consumer = null;
        }

        if (!this.consumer) {
            this.consumer = createConsumer(this.buildUrl(token));
            this.currentToken = token;
        }

        return this.consumer;
    }

    /**
     * Disconnect and destroy the current consumer instance.
     *
     * @method disconnect
     */
    disconnect() {
        if (this.consumer) {
            this.consumer.disconnect();
            this.consumer = null;
            this.currentToken = null;
        }
    }

    /**
     * Reset the consumer (alias for disconnect).
     * Used on logout or saga restart.
     *
     * @method reset
     */
    reset() {
        this.disconnect();
    }
}

export default CableConsumer;
